// crewbit-admin/src/components/money/settings/SettingsLandingMenu.tsx
//
// Landing-screen menu — 4 group cards in a responsive grid (2-up on wide,
// 1-up on narrow). Each card derives its pill state from live envelopes
// (useUserPreferences + useBusinessFinancialSettings).
//
// Sub-route navigation: clicking a card pushes /money/settings/{paid,taxes,
// people,notifications}. Browser back returns to landing.
//
// Mirrors crewbit-mobile/components/financials/MoneySettings.tsx (landing
// composition + pill derivation).

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Bell, ChevronRight, DollarSign, Image, Receipt, Users } from 'lucide-react';
import { palette, radii, spacing, typography } from '../../../styles/tokens';
import {
  useBusinessFinancialSettings,
  type StripeConnectStatus,
} from '../../../hooks/useBusinessFinancialSettings';
import { useUserPreferences } from '../../../hooks/useUserPreferences';
import SettingsStatusPill, { type SettingsStatusPillKind } from './SettingsStatusPill';

interface GroupPill {
  kind: SettingsStatusPillKind;
  label: string;
}

interface CardConfig {
  to: string;
  Icon: typeof DollarSign;
  title: string;
  description: string;
  pills: GroupPill[];
}

function stripePill(status: StripeConnectStatus): GroupPill {
  switch (status) {
    case 'active':
      return { kind: 'active', label: 'Stripe connected' };
    case 'charges_disabled':
    case 'payouts_disabled':
      return { kind: 'invite', label: 'Stripe needs attention' };
    case 'not_connected':
      return { kind: 'invite', label: 'Stripe not connected' };
  }
}

interface SettingsLandingMenuProps {
  businessId: string | null;
}

export default function SettingsLandingMenu({ businessId }: SettingsLandingMenuProps) {
  const settings = useBusinessFinancialSettings(businessId);
  const prefs = useUserPreferences();

  const notificationCount = useMemo(() => {
    if (!prefs.data) return 0;
    return [
      prefs.data.notificationPaymentsReceived,
      prefs.data.notificationDisputesOpened,
      prefs.data.notificationTaxDue,
      prefs.data.notificationPayoutsArrived,
    ].filter(Boolean).length;
  }, [prefs.data]);

  const data = settings.data;

  const paidPills: GroupPill[] = data ? [stripePill(data.stripeConnectStatus)] : [];

  const taxesPills: GroupPill[] = data
    ? [
        data.hstNumber
          ? { kind: 'active', label: 'HST set' }
          : { kind: 'invite', label: 'HST missing' },
        {
          kind: data.taxMode ? 'active' : 'empty',
          label:
            data.taxMode === 'quick_method'
              ? 'Quick Method'
              : data.taxMode === 'exempt'
                ? 'Exempt'
                : data.taxMode === 'charge'
                  ? 'Charge tax'
                  : 'Mode unset',
        },
      ]
    : [];

  const peoplePills: GroupPill[] = [{ kind: 'learn', label: 'Accountant access' }];

  const notificationsPills: GroupPill[] = [
    {
      kind: notificationCount > 0 ? 'active' : 'empty',
      label: `${notificationCount} on`,
    },
  ];

  const cards: CardConfig[] = [
    {
      to: '/money/settings/paid',
      Icon: DollarSign,
      title: 'Getting paid',
      description: 'Stripe Connect, accepted methods, late-fee policy.',
      pills: paidPills,
    },
    {
      to: '/money/settings/taxes',
      Icon: Receipt,
      title: 'Taxes',
      description: 'HST/GST registration, tax mode, mileage rates.',
      pills: taxesPills,
    },
    {
      to: '/money/settings/people',
      Icon: Users,
      title: 'People',
      description: 'Accountant invites and team money permissions.',
      pills: peoplePills,
    },
    {
      to: '/money/settings/notifications',
      Icon: Bell,
      title: 'Notifications',
      description: 'Payments, disputes, tax due, payout alerts.',
      pills: notificationsPills,
    },
    {
      to: '/money/settings/branding',
      Icon: Image,
      title: 'Branding',
      description: 'Logo, accent colour, footer text, and e-transfer.',
      pills: [],
    },
  ];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: spacing.md,
    }}>
      {cards.map((card) => (
        <Link
          key={card.to}
          to={card.to}
          style={{
            display: 'flex', alignItems: 'flex-start', gap: spacing.md,
            background: palette.white, borderRadius: radii.lg,
            border: `1px solid ${palette.g150}`,
            padding: spacing.md, textDecoration: 'none',
            transition: 'border-color 150ms ease, transform 150ms ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = palette.accentSoft;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = palette.g150;
          }}
        >
          <div style={{
            width: 40, height: 40, borderRadius: 20,
            background: palette.accentWash,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <card.Icon size={20} color={palette.accent} strokeWidth={2} />
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: spacing.xs, minWidth: 0 }}>
            <div style={{ fontSize: typography.size.heading, fontWeight: 700, color: palette.g800 }}>
              {card.title}
            </div>
            <div style={{ fontSize: typography.size.caption, color: palette.g500, lineHeight: 1.4 }}>
              {card.description}
            </div>
            {card.pills.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xxs }}>
                {card.pills.map((pill, idx) => (
                  <SettingsStatusPill
                    key={`${pill.kind}-${idx}`}
                    kind={pill.kind}
                    label={pill.label}
                  />
                ))}
              </div>
            )}
          </div>
          <ChevronRight size={18} color={palette.g400} style={{ flexShrink: 0, marginTop: 4 }} />
        </Link>
      ))}
    </div>
  );
}
